import React, { useCallback, useMemo } from 'react';
import { startOfMonth, endOfMonth, eachDayOfInterval, format } from 'date-fns';
import CalendarDay from './CalendarDay';
import {
  Lesson,
  DailyRosterInstructor
} from '@/constants/Interfaces/CommonInterfaces';
import { CalendarEvent } from '@/hooks';

interface CalendarGridProps {
  currentMonth: Date;
  events: CalendarEvent[];
  onLessonClick?: (lesson: Lesson, instructor: DailyRosterInstructor) => void;
  onDayClick?: (date: Date) => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentMonth,
  events,
  onLessonClick,
  onDayClick
}) => {
  // Generate calendar days
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);

    // Get the start of the week for the first day of the month
    const startOfWeek = new Date(start);
    startOfWeek.setDate(start.getDate() - start.getDay());

    // Get the end of the week for the last day of the month
    const endOfWeek = new Date(end);
    endOfWeek.setDate(end.getDate() + (6 - end.getDay()));

    return eachDayOfInterval({ start: startOfWeek, end: endOfWeek });
  }, [currentMonth]);

  // Get events for a specific day
  const getEventsForDay = useCallback(
    (date: Date) => {
      const dateString = format(date, 'yyyy-MM-dd');
      return events.filter(event => event.date === dateString);
    },
    [events]
  );

  // Day names
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className='overflow-auto'>
      <div className='min-w-[800px]'>
        {/* Day Headers */}
        <div className='grid grid-cols-7 border-b border-gray-200'>
          {dayNames.map(day => (
            <div
              key={day}
              className='p-3 text-center text-sm font-medium text-gray-500 bg-gray-50'
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className='grid grid-cols-7'>
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDay(day);

            return (
              <CalendarDay
                key={index}
                day={day}
                currentMonth={currentMonth}
                events={dayEvents}
                onLessonClick={onLessonClick}
                onDayClick={onDayClick}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CalendarGrid;
